<template>
  <Component
      ref="list"
      :is="tag"
      :class="classes"
  >
    <li
        v-for="(item, inx) in listItems"
        :key="`list-item-${inx}`"
    >
      <span class="marker">
        <span
            v-if="isBigIcon"
            :key="icon"
            class="icon-bg"
            :class="`icon-bg-${iconColor}`"
        >
          <div
              class="second-bg"
              :class="`${iconColor}`"
          >
            <FontAwesomeIcon :icon="getItemIcon(item)"/>
          </div>
        </span>
        <span v-else>
          <FontAwesomeIcon v-if="type === 'icon'" :icon="getItemIcon(item)" />
          <FontAwesomeIcon v-else-if="type === 'ul'" icon="fa-solid fa-circle" />
          <span v-else >{{ inx + 1 }}.</span>
        </span>
      </span>
      <span
          class="item"
          :class="iconColor !== 'dark' ? 'category-icon' : ''"
      >
        <span v-html="getContent(item)"></span>
        <Tooltip v-if="hasInfoText(item)">
          &nbsp;<FontAwesomeIcon icon="fa-light fa-info-circle"/>
          <template #title><span v-html="getInfoText(item)"></span></template>
        </Tooltip>
      </span>
    </li>
  </Component>
</template>

<script lang="ts" setup>
import Tooltip from '@/components/Tooltip.vue';
import {
  computed,
  ref,
  useAttrs,
} from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck as falCheck,
  faInfoCircle as falInfoCircle,
} from '@fortawesome/pro-light-svg-icons';
import {
  faCheck as fasCheck,
  faCircle as fasCircle,
} from '@fortawesome/pro-solid-svg-icons';

library.add(falCheck, fasCheck, falInfoCircle, fasCircle);

const props = withDefaults(defineProps<{
  type?: string
  icon?: string
  iconColor?: string
  fontSize?: string
  items?: string | string[]
  gridSize?: `${number}`
  small?: boolean
}>(), {
  type: 'ul',
  icon: 'fas fa-check',
  iconColor: 'dark',
  fontSize: 'regular',
  items: () => btoa(JSON.stringify([''])),
  gridSize: '1',
});

const isBigIcon = props.type === 'icon' && props.iconColor !== 'dark';

const attrs = useAttrs();

const tag = computed(() => (props.type === 'icon' ? 'ul' : props.type));
const list = ref<HTMLElement>();
const listItems = computed<string[]>(() => (Array.isArray(props.items) ? props.items : JSON.parse(atob(props.items))).filter((item: string | null) => `${item || ''}`.trim() !== ''));
const mapIcon = (iconString: string | undefined) => {
  if (iconString) {
    return iconString
      .replace('fas ', 'fa-solid ')
      .replace('fal ', 'fa-light ');
  }

  return 'fa-solid fa-check';
};
const mappedIcon = computed<string>(() => mapIcon(props.icon));
const getItemIcon = (listItem: string | string[]) => {
  if (Array.isArray(listItem) && listItem[2]) {
    return mapIcon(listItem[2]);
  }

  return mappedIcon.value;
};

const getContent = (item: string | string[]) => {
  if (Array.isArray(item)) {
    return item[0];
  }

  return item;
};

const hasInfoText = (item: string | string[]) => Array.isArray(item) && item.length >= 2 && item[1];
const getInfoText = (item: string | string[]) => (hasInfoText(item) ? item[1] : '');

const classes = computed(() => {
  const listClasses = [
    'io-list',
    `io-list--${props.type}`,
    `io-list--icon-${props.iconColor}`,
    `fs-${props.fontSize}`,
  ];

  if (typeof attrs.class === 'string') {
    listClasses.push(attrs.class);
  }

  if (props.type === 'icon' && props.iconColor !== 'dark') {
    listClasses.push('big-gap');
  }

  if (parseInt(props.gridSize, 10) === 2) {
    listClasses.push('io-list--grid');
  }

  if (props.small) {
    listClasses.push('small');
  }

  return listClasses;
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.io-list {
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 100%;
  gap: 10px;
  list-style-type: none;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    gap: 20px 40px;
  }

  &.big-gap {
    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      gap: 30px 40px;
    }
  }

  &.small {
    font-size: .75rem;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: .9375rem;
    }
  }

  &--grid {
    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      grid-template-columns: 1fr 1fr;
      gap: 40px;
    }
  }

  > li {
    display: grid;
    gap: 10px;
    align-items: start;
    grid-template-columns: auto 1fr;
    padding: 0;
    margin: 0;
    @include font-weight-fix;

    .item {
      &.category-icon {
        position: relative;
      }
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:last-child {
      margin-bottom: 0 !important;
    }
  }

  .icon-bg {
    border-radius: 65px;
    width: 2.22222em;
    height: 2.22222em;
    left: 0;
    color: #FFFFFF;
    display: block;
    position: relative;

    &.icon-bg-dark {
      background: transparent;
      color: #38383A;
    }

    &:before, &:after {
      content: '';
      position: absolute;
      border-radius: 10em;
    }

    &:before {
      inset: 0;
      z-index: 1;
      opacity: .5;
    }

    &:after {
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 2;
      width: 75%;
      height: 75%;
    }

    > * {
      position: absolute;
      font-size: 1.11111em;
      z-index: 3;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
    }
  }

  &--icon-dark {
    > li {
      .fa-li-icon {
        border-radius: inherit;
        width: auto;
        height: auto;

        > * {
          left: inherit;
          top: inherit;
          transform: none;
        }
      }
    }
  }
}
</style>
